import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  CalendarIcon, 
  ClockIcon, 
  MapPinIcon,
  EyeIcon,
  PlusIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import toast from 'react-hot-toast';
import { getCustomerPortalErrorMessage } from '../utils/apiErrorUtils';
import { 
  getJobStatusColor, 
  getJobStatusLabel,
  mapStatusForCustomer,
  JOB_STATUS_OPTIONS,
  canReRequestJob 
} from '../utils/statusConstants';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const Appointments = () => {
  const { makeAuthenticatedRequest } = useAuth();
  const { conversations, unreadByConversation } = useChat();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({
    status: searchParams.get('requiresAuthorization') === 'true' ? 'pending_authorization' : (searchParams.get('status') || 'all'),
    jobType: 'all',
    search: ''
  });
  const [currentPage, setCurrentPage] = useState(1);

  const loadAppointments = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        ...(filters.status !== 'all' && filters.status !== 'pending_authorization' && { status: filters.status }),
        ...(filters.jobType !== 'all' && { jobType: filters.jobType }),
        ...(filters.search && { search: filters.search }),
        ...(filters.status === 'pending_authorization' && { requiresAuthorization: 'true' })
      });

      console.log('Search params:', params.toString());
      const data = await makeAuthenticatedRequest(`/appointments?${params}`);
      
      if (data.success) {
        setAppointments(data.data.appointments);
        setPagination(data.data.pagination);
      } else {
        toast.error(data.message || 'Failed to load appointments');
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast.error(getCustomerPortalErrorMessage(error, 'Failed to load appointments'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters.status, filters.jobType, filters.search, makeAuthenticatedRequest]);

  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.search]);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadAppointments();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters.search, loadAppointments]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Not scheduled';
    
    try {
      // Extract date parts to avoid timezone conversion
      // If date is in format "2025-10-24T00:00:00.000Z" or "2025-10-24", extract just the date part
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(num => parseInt(num, 10));
      
      // Create date in local timezone (no UTC conversion)
      const localDate = new Date(year, month - 1, day);
      
      // Check if the date is valid
      if (isNaN(localDate.getTime())) {
        return 'Invalid Date';
      }
      
      return localDate.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };


  return (
    <div className="p-6">
      <div className="max-w-7xl">
        {/* Enhanced Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl shadow-lg p-8 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-white mb-2">Appointments</h1>
                <p className="text-green-100 text-lg">Manage and track your interpreter appointments</p>
              </div>
              <button
                onClick={() => navigate('/appointments/new')}
                className="bg-white text-green-600 px-6 py-3 rounded-lg hover:bg-green-50 flex items-center font-semibold transition-colors"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                New Appointment
              </button>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6 bg-white rounded-xl shadow-lg border border-gray-100 p-6"
        >
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-400" />
              <select
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {JOB_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <select
                value={filters.jobType}
                onChange={(e) => setFilters({ ...filters, jobType: e.target.value })}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="interpreting">Interpreting</option>
                <option value="transportation">Transportation</option>
              </select>
            </div>
            
            <div className="flex-1 max-w-sm">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  placeholder="Search appointments..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Enhanced Appointments List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
        >
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : appointments.length === 0 ? (
            <div className="text-center py-12">
              <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No appointments found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by scheduling your first appointment.
              </p>
              <button
                onClick={() => navigate('/appointments/new')}
                className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
              >
                Schedule Appointment
              </button>
            </div>
          ) : (
            <div className="overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    {filters.status === 'pending_authorization' 
                      ? `${appointments.length} Appointment${appointments.length !== 1 ? 's' : ''} Requiring Authorization`
                      : `${pagination.total} Appointment${pagination.total !== 1 ? 's' : ''}`
                    }
                  </h3>
                  {filters.status === 'pending_authorization' && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
                      <ClockIcon className="h-4 w-4 mr-1" />
                      Pending Your Authorization
                    </span>
                  )}
                </div>
              </div>
              
              <div className="divide-y divide-gray-200">
                {appointments.map((appointment) => (
                  <div 
                    key={appointment.id} 
                    className="p-6 hover:bg-gray-50 cursor-pointer transition-colors duration-200"
                    onClick={() => navigate(`/appointments/${appointment.id}`)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-medium text-gray-900">
                              {appointment.job_number || `Job #${appointment.id.slice(-6)}`}
                            </h3>
                            {(() => {
                              const conv = conversations?.find(c => c.job_id === appointment.id);
                              const unread = conv ? (unreadByConversation[conv.id] || 0) : 0;
                              return unread > 0 ? (
                                <span className="inline-flex items-center justify-center rounded-full bg-blue-600 text-white text-xs font-bold px-2 py-0.5">
                                  {unread}
                                </span>
                              ) : null;
                            })()}
                          </div>
                          {appointment.title && appointment.title !== appointment.job_number && (
                            <p className="text-sm text-gray-600 mt-1">
                              {appointment.title}
                            </p>
                          )}
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getJobStatusColor(appointment)}`}>
                              {getJobStatusLabel(appointment)}
                            </span>
                            {appointment.customer_authorization_required && !appointment.customer_authorization_obtained && appointment.can_authorize && 
                              !['completed', 'cancelled', 'closed', 'billed', 'interpreter_paid', 'no_show', 'rejected', 'completion_report'].includes(appointment.status) && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-300">
                                <ClockIcon className="h-3 w-3 mr-1" />
                                Needs Authorization
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="flex items-center text-sm text-gray-500">
                            <CalendarIcon className="h-4 w-4 mr-2" />
                            {formatDate(appointment.scheduled_date)}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <ClockIcon className="h-4 w-4 mr-2" />
                            {formatTime(appointment.scheduled_time)} 
                            {appointment.arrival_time && (
                              <span className="ml-2 text-blue-600">
                                (Arrive: {formatTime(appointment.arrival_time)})
                              </span>
                            )}
                          </div>
                          <div className="flex items-center text-sm text-gray-500">
                            <MapPinIcon className="h-4 w-4 mr-2" />
                            {appointment.is_remote ? 'Remote' : (
                              appointment.location_address 
                                ? appointment.location_address 
                                : (appointment.location_city || appointment.location_state 
                                  ? `${appointment.location_city || ''}${appointment.location_city && appointment.location_state ? ', ' : ''}${appointment.location_state || ''}`.trim()
                                  : 'Location not specified')
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-6">
                            <div>
                              <span className="font-medium text-gray-700">Claimant:</span>
                              <span className="ml-1 text-gray-900">
                                {appointment.claimant_first_name} {appointment.claimant_last_name}
                              </span>
                            </div>
                            {appointment.claim_number && (
                              <div>
                                <span className="font-medium text-gray-700">Claim:</span>
                                <span className="ml-1 text-gray-900">{appointment.claim_number}</span>
                              </div>
                            )}
                            {appointment.service_type_name && (
                              <div>
                                <span className="font-medium text-gray-700">Service:</span>
                                <span className="ml-1 text-gray-900">{appointment.service_type_name}</span>
                              </div>
                            )}
                            {appointment.language_name && (
                              <div>
                                <span className="font-medium text-gray-700">Language:</span>
                                <span className="ml-1 text-gray-900">{appointment.language_name}</span>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center space-x-4" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => navigate(`/appointments/${appointment.id}`)}
                              className="text-blue-600 hover:text-blue-500 flex items-center"
                            >
                              <EyeIcon className="h-4 w-4 mr-1" />
                              View Details
                            </button>
                            {canReRequestJob(mapStatusForCustomer(appointment.status)) && (
                              <button
                                onClick={() => navigate(`/appointments/new?reRequest=${appointment.id}&returnTo=/appointments`)}
                                className="text-green-600 hover:text-green-500 flex items-center"
                              >
                                <ArrowPathIcon className="h-4 w-4 mr-1" />
                                Re-request
                              </button>
                            )}
                          </div>
                        </div>
                        
                        {appointment.interpreter_first_name && (
                          <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                            <div className="flex items-center text-sm">
                              <span className="font-medium text-blue-900">Interpreter Assigned:</span>
                              <span className="ml-2 text-blue-800">
                                {appointment.interpreter_first_name} {appointment.interpreter_last_name}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Showing {((currentPage - 1) * pagination.limit) + 1} to {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-2 text-sm">
                      Page {currentPage} of {pagination.total_pages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(pagination.total_pages, currentPage + 1))}
                      disabled={currentPage === pagination.total_pages}
                      className="px-3 py-2 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Appointments;
